import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Eye, EyeOff, Loader2, ExternalLink } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import { getStaffKingsChatLoginUrl } from "../lib/config";

export function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, setUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    portal_id: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Force light mode on login page
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    return () => {
      // Don't restore theme on unmount - let ThemeProvider handle it after login
    };
  }, []);

  const handleTokenLogin = async (token) => {
    setRedirecting(true);
    setLoading(true);
    setError("");

    try {
      // Store token
      localStorage.setItem('auth_token', token);

      // Fetch staff data
      const response = await api.get('/me');
      
      if (response.data.success && response.data.data) {
        const staffData = { ...response.data.data, type: 'staff' };
        localStorage.setItem('user', JSON.stringify(staffData));
        
        // Update AuthContext state immediately so isAuthenticated becomes true
        setUser(staffData);
        
        // Get redirect path from URL or localStorage, default to home
        // Ensure staff only redirects to staff routes (not admin routes)
        let redirectPath = searchParams.get('redirect') || localStorage.getItem('intended_redirect') || '/';
        localStorage.removeItem('intended_redirect');
        
        // Validate redirect path - staff should not access admin routes
        if (redirectPath.startsWith('/pilot')) {
          redirectPath = '/'; // Force staff to home if trying to access admin route
        }
        
        // Use window.location.href to force full page reload and ensure AuthContext re-initializes
        // This prevents the double login issue
        window.location.href = redirectPath;
      } else {
        setError('Failed to fetch staff data');
        setRedirecting(false);
      }
    } catch (err) {
      const raw = err.response?.data?.message ?? err?.message;
      const errorMessage = typeof raw === 'string' ? raw : (raw?.errors ? Object.values(raw.errors).flat().join(' ') : 'Failed to authenticate');
      setError(errorMessage);
      console.error('Token login error:', err);
      setRedirecting(false);
      // Clear URL params on error
      window.history.replaceState({}, document.title, '/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check for token from backend redirect
    const token = searchParams.get('token');
    const isStaff = searchParams.get('isStaff');
    const errorParam = searchParams.get('error');
    const messageParam = searchParams.get('message');
    const redirect = searchParams.get('redirect');

    // Store redirect parameter if provided
    if (redirect) {
      localStorage.setItem('intended_redirect', redirect);
    }

    if (searchParams.get('reset') === 'success') {
      setError('');
      setInfoMessage('Your password was updated. You can sign in now.');
      window.history.replaceState({}, document.title, '/login');
      return;
    }

    if (errorParam) {
      setError(decodeURIComponent(messageParam || 'Authentication failed'));
      // Clear URL params
      window.history.replaceState({}, document.title, '/login');
      return;
    }

    if (token && isStaff === 'true') {
      handleTokenLogin(token);
    }
  }, [searchParams]);

  const handleKingsChatLogin = () => {
    // Get intended redirect from URL or localStorage
    const redirect = searchParams.get('redirect') || localStorage.getItem('intended_redirect') || '/';
    if (redirect) {
      localStorage.setItem('intended_redirect', redirect);
    }
    // Redirect to API web route GET /staff/login (staffLoginLink) → KingsChat OAuth
    window.location.href = getStaffKingsChatLoginUrl(redirect || undefined);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");
    setLoading(true);

    try {
      const data = await login(formData.portal_id, formData.password);

      // Staff must verify KingsChat before using the app if they have no kcuserid
      if (data?.data?.requires_kc_verification || data?.data?.user?.requires_kc_verification) {
        navigate('/profile?verify_required=1');
        return;
      }

      // Get redirect path from URL or localStorage, default to home
      // Ensure staff only redirects to staff routes (not admin routes)
      let redirectPath = searchParams.get('redirect') || localStorage.getItem('intended_redirect') || '/';
      localStorage.removeItem('intended_redirect');

      // Validate redirect path - staff should not access admin routes
      if (redirectPath.startsWith('/pilot')) {
        redirectPath = '/'; // Force staff to home if trying to access admin route
      }

      navigate(redirectPath);
    } catch (err) {
      const msg = err?.message ?? err?.response?.data?.message;
      const text = typeof msg === 'string' ? msg : (msg && typeof msg === 'object' && msg.errors ? Object.values(msg.errors).flat().join(' ') : 'Login failed. Please check your credentials.');
      setError(text);
    } finally {
      setLoading(false);
    }
  };

  // Generate animated circles
  const circles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    size: Math.random() * 200 + 50,
    left: Math.random() * 100,
    top: Math.random() * 100,
    delay: Math.random() * 10,
    duration: Math.random() * 20 + 15,
  }));

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#1558D6] via-[#2D7AFF] to-[#5B9AFF] p-4 relative overflow-hidden">
      {/* Animated Background Circles */}
      <div className="absolute inset-0 overflow-hidden">
        {circles.map((circle) => (
          <div
            key={circle.id}
            className="absolute rounded-full opacity-35 animate-float"
            style={{
              width: `${circle.size}px`,
              height: `${circle.size}px`,
              left: `${circle.left}%`,
              top: `${circle.top}%`,
              background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 70%)',
              animationDelay: `${circle.delay}s`,
              animationDuration: `${circle.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Login Card or Redirecting Card */}
      <div className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 relative z-10">
        {redirecting ? (
          /* Redirecting Card */
          <div className="flex flex-col items-center justify-center py-12">
            <div className="mb-6">
              <div className="size-20 rounded-2xl bg-white p-2 flex items-center justify-center shadow-lg shadow-[#1E74FD]/50 mx-auto ring-2 ring-[#1E74FD]/20">
                <img src="/favicon.png" alt="" className="size-full object-contain" aria-hidden />
              </div>
              <Loader2 className="size-8 text-[#1E74FD] animate-spin mx-auto mt-4" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">Redirecting...</h1>
            <p className="text-gray-600 text-center">Please wait while we log you in</p>
          </div>
        ) : (
          <>
            {/* Logo and Title */}
            <div className="flex flex-col items-center mb-8">
              <div className="mb-4">
                <div className="size-20 rounded-2xl bg-white p-2 flex items-center justify-center shadow-lg shadow-[#1E74FD]/50 ring-2 ring-[#1E74FD]/20">
                  <img src="/favicon.png" alt="LW Staff Portal" className="size-full object-contain" />
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back!</h1>
              <p className="text-gray-600">Please sign in to continue</p>
            </div>
            {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {infoMessage && (
            <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-xl">
              {infoMessage}
            </div>
          )}

          {error && typeof error === 'string' && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl flex items-center gap-2">
              <div className="size-1.5 rounded-full bg-red-500 animate-pulse" />
              {error}
            </div>
          )}

          {loading && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded-xl flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              Authenticating with KingsChat...
            </div>
          )}

          {/* Portal ID Input */}
          <div>
            <label htmlFor="portal_id" className="block text-sm font-medium text-gray-700 mb-2">
              Portal ID
            </label>
            <input
              id="portal_id"
              type="text"
              value={formData.portal_id}
              onChange={(e) => setFormData({ ...formData, portal_id: e.target.value })}
              onFocus={() => setFocusedField("portal_id")}
              onBlur={() => setFocusedField(null)}
              className={`w-full px-4 py-3 rounded-xl border-2 transition-colors bg-gray-50 focus:bg-white ${
                focusedField === "portal_id" 
                  ? "border-[#1E74FD] focus:outline-none" 
                  : "border-gray-200 focus:border-[#1E74FD] focus:outline-none"
              }`}
              placeholder="Enter your portal ID"
              required
              disabled={loading}
            />
          </div>

          {/* Password Input */}
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <label htmlFor="password" className="text-sm font-medium text-gray-700">
                Password
              </label>
              <Link
                to="/login/forgot-password"
                className="text-sm font-medium text-[#1E74FD] hover:text-[#1558D6] hover:underline shrink-0"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField(null)}
                className={`w-full px-4 py-3 rounded-xl border-2 transition-colors bg-gray-50 focus:bg-white pr-12 ${
                  focusedField === "password" 
                    ? "border-[#1E74FD] focus:outline-none" 
                    : "border-gray-200 focus:border-[#1E74FD] focus:outline-none"
                }`}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={loading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>


          {/* Login Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1E74FD] text-white py-3 rounded-xl font-semibold hover:bg-[#1558D6] transition-colors shadow-lg shadow-[#1E74FD]/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">or</span>
            </div>
          </div>

          {/* KingsChat Login Button */}
          <button
            type="button"
            onClick={handleKingsChatLogin}
            disabled={loading}
            className="w-full bg-white border-2 border-gray-200 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-3 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <img 
              src="https://kingsch.at/h/css/images/favicon.ico" 
              alt="KingsChat" 
              className="size-5"
            />
            <span>Continue with KingsChat</span>
            <ExternalLink className="size-4 opacity-50" />
          </button>
        </form>
          </>
        )}
      </div>

      {/* Add CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
          }
          33% {
            transform: translateY(-30px) translateX(20px);
          }
          66% {
            transform: translateY(30px) translateX(-20px);
          }
        }
        .animate-float {
          animation: float linear infinite;
        }
      `}</style>
    </div>
  );
}
